import React, { useState } from "react";
import { Loader2, AlertTriangle, Trash2 } from "lucide-react";
import { api } from "../lib/api";

/**
 * Destructive "Erase All" button with mandatory password reconfirm.
 *
 * Usage:
 *   <EraseAllButton
 *     scope="inventory"
 *     label="Erase All Inventory"
 *     confirmText="every inventory row"
 *     onDone={() => reload()}
 *   />
 *
 * Scopes correspond to backend POST /api/admin/erase/{scope}:
 *   members, quotes, inventory, transactions
 */
export default function EraseAllButton({ scope, label, confirmText, onDone, testid }) {
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState("");

  const erase = async (e) => {
    e?.preventDefault();
    setErr(""); setBusy(true);
    try {
      const r = await api.post(`/admin/erase/${scope}`, { password: pw });
      const counts = Object.entries(r.data.deleted || {})
        .map(([k, v]) => `${v} ${k}`).join(", ");
      setDone(counts || "done");
      setPw("");
      setTimeout(() => { setOpen(false); setDone(""); onDone?.(); }, 900);
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Erase failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => { setOpen(true); setErr(""); setDone(""); setPw(""); }}
        className="inline-flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-widest font-bold"
        style={{ border: "1px solid var(--ab-danger)", color: "var(--ab-danger)", background: "rgba(239,68,68,0.05)" }}
        data-testid={testid || `erase-${scope}-btn`}
      >
        <Trash2 size={12} /> {label || `Erase All`}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-6"
          style={{ background: "rgba(0,0,0,0.8)", backdropFilter: "blur(4px)" }}
          onClick={() => !busy && setOpen(false)}
          data-testid={`erase-${scope}-modal`}
        >
          <form
            onSubmit={erase}
            onClick={(e) => e.stopPropagation()}
            className="surface p-7 max-w-md w-full"
          >
            <div className="flex items-center gap-3 mb-3" style={{ color: "var(--ab-danger)" }}>
              <AlertTriangle size={22} />
              <h3 className="font-display text-2xl font-bold">{label || "Erase All"}</h3>
            </div>
            <p className="text-sm mb-5" style={{ color: "var(--ab-text-muted)" }}>
              You are about to permanently delete <b>{confirmText}</b>. This action cannot be undone.
              Re-enter the POS password to confirm.
            </p>
            <input
              type="password"
              autoFocus
              className="input-terminal mb-3"
              placeholder="POS password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              data-testid={`erase-${scope}-password`}
            />
            {err && <div className="text-sm mb-3" style={{ color: "var(--ab-danger)" }} data-testid={`erase-${scope}-error`}>{err}</div>}
            {done && <div className="text-sm mb-3" style={{ color: "var(--ab-primary)" }} data-testid={`erase-${scope}-done`}>Deleted: {done}</div>}
            <div className="flex gap-3 justify-end">
              <button type="button" onClick={() => setOpen(false)} disabled={busy} className="btn-secondary" data-testid={`erase-${scope}-cancel`}>Cancel</button>
              <button type="submit" disabled={busy || !pw} className="btn-danger inline-flex items-center gap-2" data-testid={`erase-${scope}-confirm`}>
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Permanently Erase
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
